/**
 * Everything about this table, in one place.
 *
 * The header used to carry seven controls in a row - rules, chat, voice, the
 * watcher count, a turn pill, a clock and Leave - and it wrapped on every
 * phone. Worse, they were seven unrelated things sharing a strip because that
 * is where there was room, not because they belonged together.
 *
 * They do belong together, though: every one of them answers "something about
 * this table" rather than "something about this turn". So the turn things stay
 * in the header where you glance at them, and the rest live here, behind one
 * button, opened deliberately.
 *
 * ## Why three dots and not a gear
 *
 * A gear promises settings and this holds the rules, the room code and the way
 * out. A hamburger promises site navigation, which a game table is not. Both
 * borrow a convention that describes different contents; dots promise only
 * "more about this", which is exactly what is here.
 *
 * It carried the word "Table" beside them at first, because the plan's rule
 * about icon buttons asks for a word. The word is gone: it made the control a
 * wide slab that read as a heading, and three dots in the corner of a screen
 * is the one glyph that rule can spare, being already understood everywhere.
 * `aria-label` still says "Table menu", so nothing was lost to anything that
 * speaks.
 *
 * ## What is NOT here
 *
 * Voice. It moves to the action row beside the game's own buttons, because
 * joining a conversation is something you do at a moment - when somebody says
 * "can you hear me" - rather than something you go looking for in a menu.
 */

import { useEffect, useState, useSyncExternalStore } from 'react';
import type { MatchView } from '../net/match.js';
import type { GameLayoutWire, VariantSpecWire } from '../net/session.js';
import type { ChatPhrase } from '../net/session.js';
import { Icon } from '../render/Icon.js';
import { houseRules } from '../render/house-rules.js';
import { HostControls } from './HostControls.js';
import { SoundSetting } from './SoundSetting.js';
import { useSession } from '../session-context.js';
import { avatarEmoji } from '../render/avatars.js';
import { SettingRow } from './SettingRow.js';
import { classicFaces, setClassicFaces, subscribeClassicFaces } from '../render/classic-faces.js';
import { isSolo } from '../net/match.js';
import { VariantControls } from './VariantControls.js';

/** One row in the menu: a mark, a name, a consequence, and what it opens. */
function MenuRow({
  icon,
  title,
  help,
  onClick,
  danger,
}: {
  icon: 'info' | 'message' | 'link' | 'eye' | 'close' | 'pair-of-cards';
  title: string;
  help?: string | undefined;
  onClick: () => void;
  danger?: boolean | undefined;
}) {
  return (
    <button
      type="button"
      className={`menu-row${danger ? ' danger' : ''}`}
      onClick={onClick}
    >
      <span className="setting-icon">
        <Icon name={icon} />
      </span>
      <span className="setting-text">
        <span className="setting-title">{title}</span>
        {help ? <span className="setting-help">{help}</span> : null}
      </span>
      <span className="menu-chevron" aria-hidden="true">
        <Icon name="chevron-right" />
      </span>
    </button>
  );
}

export function TableMenu({
  view,
  layout,
  roomCode,
  gameName,
  variantSpecs,
  phrases,
  limits,
  autoRetreat,
  onAutoRetreat,
  onHostAction,
  onRules,
  onChat,
  onLeave,
  onWear,
  onDress,
  onClose,
}: {
  view: MatchView;
  layout: GameLayoutWire;
  roomCode: string;
  gameName: string;
  variantSpecs: VariantSpecWire[];
  phrases: readonly ChatPhrase[];
  limits?: { min: number; default: number; max: number } | undefined;
  autoRetreat: boolean;
  onAutoRetreat: (next: boolean) => void;
  onHostAction: (action: Record<string, unknown>) => void;
  onRules: () => void;
  onChat: () => void;
  onLeave: () => void;
  /** Wear one of the faces this table lends (D92). */
  onWear: (avatarId: string) => void;
  /** Open the host's cards-and-table sheet, which replaces this menu. */
  onDress: () => void;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const classic = useSyncExternalStore(subscribeClassicFaces, classicFaces, () => false);
  const setClassic = (next: boolean) => {
    setClassicFaces(next);
  };

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  /*
   * Typing is the host's to allow; the phrases are not.
   *
   * "Off leaves the quick phrases" has been the rule since chat was built, and
   * it is why these are two rows rather than one - a table with typing off
   * still has a way to say "nice hand".
   */
  const canType = view.allowTextChat && view.seat !== null;
  const canSpeak = view.seat !== null;
  /* See `isSolo`. A patience has nobody to talk to and no turn to hide
     between, so neither section is offered rather than being offered empty. */
  const solo = isSolo(view);

  return (
    <div
      /* Solid: this is a screen you read instead of the table, not against it. */
      className="sheet sheet-solid"
      role="dialog"
      aria-modal="true"
      aria-label="Table"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="sheet-body table-menu">
        <div className="sheet-head">
          <p className="sheet-title">Table</p>
          <button type="button" className="sheet-close" onClick={onClose} aria-label="Close">
            <Icon name="close" />
          </button>
        </div>

        {/*
          What you are playing and under what rules, in the one place somebody
          goes looking. The summary is the same one on the game line, so a
          player who noticed "2 house rules" up there can come here and find
          out which two.
        */}
        <div className="menu-section">
          <p className="menu-section-title">{gameName}</p>
          <MenuRow
            icon="info"
            title="How to play"
            help={houseRules(variantSpecs, view.variants)}
            onClick={onRules}
          />

          {/*
            Whose cards these are (D87).

            The deck and the table belong to the room and the host supplies
            them, so somebody looking at a painted table should be able to find
            out who paid for it - a purchase nobody can tell is a purchase
            sells the next one to nobody. Quiet, and absent on the free theme:
            crediting somebody for the cards everybody already has would make
            the free tier read as a purchase that failed.
          */}
          {view.skin?.by ? (
            <p className="menu-note">Played with {view.skin.by}&apos;s cards.</p>
          ) : null}
        </div>

        {/*
          House rules, for a table that never sees a lobby.

          Every other game sets these before the deal, on a screen a patience
          skips - so without this a solitaire player could read which rules
          they were playing under and never change them. Changing one here
          deals again under it, which is the server's rule and the only
          sensible one: the deal in front of you was built from the old answer.
        */}
        {solo && view.isHost && variantSpecs.length > 0 ? (
          <div className="menu-section">
            <p className="menu-section-title">Game options</p>
            <p className="menu-note">Changing one of these deals a new game.</p>
            <VariantControls specs={variantSpecs} view={view} onHostAction={onHostAction} />
          </div>
        ) : null}

        {solo ? null : (
        <div className="menu-section">
          <p className="menu-section-title">Talking</p>
          {canSpeak ? (
            <MenuRow
              icon="message"
              title={canType ? 'Chat' : 'Quick phrases'}
              help={
                canType
                  ? `${String(phrases.length)} quick phrases, or type your own`
                  : 'The host has typing off'
              }
              onClick={onChat}
            />
          ) : (
            <p className="menu-note">
              Watching, so no chair to speak from — a seat is what a remark hangs above.
            </p>
          )}
        </div>
        )}

        {/*
          The code, mid-game.

          H10 put it behind an affordance rather than always on screen, and
          this is the affordance: somebody arriving late needs it read out, and
          the person reading it out is usually mid-hand.
        */}
        <div className="menu-section">
          <p className="menu-section-title">Room</p>
          <div className="menu-code">
            <span className="menu-code-label">Room code</span>
            <span className="menu-code-value">{roomCode}</span>
            <button
              type="button"
              className="icon-button quiet"
              onClick={() => {
                void (async () => {
                  try {
                    await navigator.clipboard.writeText(roomCode);
                    setCopied(true);
                    window.setTimeout(() => setCopied(false), 2000);
                  } catch {
                    // Clipboard can be refused outright; the code is on screen
                    // above regardless, which is the fallback that always works.
                  }
                })();
              }}
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>

        {/*
          Whose faces are on offer here (D92).

          Between the room and the device settings, because it belongs to
          neither list. It is not a preference that follows you from table to
          table - it is this table's, like the code above it - and among the
          switches it split "On this device" in two.
        */}
        <BorrowedFaces view={view} onWear={onWear} />

        {/*
          The host's deck and table, changeable without leaving the game.

          Host only, because the deck and the table belong to the room and come
          from the host (D87). A guest changing theirs would be changing
          nothing they could see, which is a control that lies. Behind a row
          rather than inline: two pickers of themes is a lot of menu for
          something most tables never touch.
        */}
        {view.isHost ? (
          <div className="menu-section">
            <p className="menu-section-title">This table</p>
            <MenuRow
              icon="pair-of-cards"
              title="Cards and table"
              help="What everyone here plays with"
              onClick={onDress}
            />
          </div>
        ) : null}

        {/*
          Yours, not the room's.

          Both of these change what happens on THIS phone - what you see and
          what you hear - rather than anything the table shares, which is why
          they sit together and away from the host's controls below. Two people
          at one table can reasonably want different answers to either.

          Sound is here because every player needs it and only the host has a
          lobby to set it in. The section shows for a patience too, where there
          is no hand to hide but there is still noise to turn off.
        */}
        <div className="menu-section">
          <p className="menu-section-title">On this device</p>

          <SoundSetting />

          {/*
            The reading override (C7), and it belongs here rather than with the
            host's controls: it changes what THIS person sees, and a player who
            needs it should never have to ask the host for it.

            Offered whatever the table is painted in. A switch that appeared
            only at a themed table would be one somebody could not find in
            advance, and the moment they needed it would be the moment a
            stranger's deck was already on screen.
          */}
          <SettingRow
            icon="pair-of-cards"
            title="Classic cards"
            help="Plain faces, whatever deck the host is using"
            checked={classic}
            onChange={setClassic}
          />

          {solo ? null : (
          <label className="setting-row">
            <span className="setting-icon">
              <Icon name="play" />
            </span>
            <span className="setting-text">
              <span className="setting-title">Hide between turns</span>
              <span className="setting-help">
                Slides your cards away while somebody else is thinking
              </span>
            </span>
            <input
              type="checkbox"
              className="setting-switch"
              checked={autoRetreat}
              onChange={(event) => onAutoRetreat(event.target.checked)}
            />
            <span className="setting-state" aria-hidden="true">
              {autoRetreat ? 'On' : 'Off'}
            </span>
          </label>
          )}
        </div>

        {/* The host's own controls, as a section rather than a second sheet. */}
        {view.isHost ? (
          <div className="menu-section">
            <p className="menu-section-title">Host</p>
            <HostControls view={view} layout={layout} limits={limits} onAction={onHostAction} />
          </div>
        ) : null}

        <div className="menu-section">
          {confirmLeave ? (
            <div className="menu-confirm">
              <button
                type="button"
                className="big-button danger"
                onClick={() => {
                  setConfirmLeave(false);
                  onLeave();
                }}
              >
                {solo
                  ? 'End it — this deal is over'
                  : view.isHost
                    ? 'Leave — hosting passes on'
                    : 'Leave — the table plays on'}
              </button>
              <button type="button" className="text-button" onClick={() => setConfirmLeave(false)}>
                Stay
              </button>
            </div>
          ) : (
            <MenuRow
              icon="close"
              /*
               * One control, and it says what it does for whoever is reading.
               *
               * There is no table to leave in a patience - there is a deal,
               * and you are the only one at it. "Leave table" left a solo
               * player looking for the table they were leaving, and sat above
               * a second control offering to end a game on everybody's behalf.
               */
              title={solo ? 'End game' : 'Leave table'}
              help={solo ? 'Goes back to your games' : 'The game carries on without you'}
              onClick={() => setConfirmLeave(true)}
              danger
            />
          )}
        </div>
      </div>

    </div>
  );
}


/**
 * The faces this table lends, and the one you came with (D92).
 *
 * Everyone adopts the host's theme while they are at the host's table, and
 * that includes its cast. You pick one for the evening and hand it back on the
 * way out - nothing here writes to a profile, which is the whole reason the
 * borrow does not become keeping.
 *
 * Absent at a table that lends nothing, which is every table painted in the
 * free theme. A picker offering only the faces everybody already has would be
 * a control with no purpose, and the day somebody buys a theme is the day it
 * appears - which is itself an advertisement.
 *
 * Your own face is first and always there, because going back to it is the
 * other half of borrowing.
 */
function BorrowedFaces({
  view,
  onWear,
}: {
  view: MatchView;
  onWear: (avatarId: string) => void;
}) {
  const { config } = useSession();
  const offered = view.skin?.avatarIds ?? [];
  if (offered.length === 0) return null;

  const mine = config?.profile?.avatarId ?? null;
  const worn = view.seats.find((seat) => seat.seat === view.seat)?.avatarId ?? mine;

  const faces = [...(mine === null ? [] : [mine]), ...offered.filter((id) => id !== mine)];

  return (
    <div className="menu-section">
      <p className="menu-section-title">Faces at this table</p>
      <p className="menu-note">
        {view.skin?.by
          ? `${view.skin.by} is lending these. They are yours for this game.`
          : 'Yours for this game.'}
      </p>
      <div className="avatar-grid">
        {faces.map((id) => (
          <button
            key={id}
            type="button"
            className={`avatar-choice${id === worn ? ' chosen' : ''}`}
            aria-label={id === mine ? 'Your own face' : `Wear the ${id}`}
            aria-pressed={id === worn}
            onClick={() => onWear(id)}
          >
            <span aria-hidden="true">{avatarEmoji(id)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
